import {
  ADDITION_AXIS_FOCAL_TYPES,
  BULK_ORDER_GRID_AXES,
  BULK_ORDER_GRID_SIGNS,
} from './config'
import { getQuantityKey, toDisplayPowers, toPlusCylinderCoords } from './gridUtils'

export { ADDITION_AXIS_FOCAL_TYPES }

export const getGridAxis = focalType =>
  ADDITION_AXIS_FOCAL_TYPES.includes(focalType)
    ? BULK_ORDER_GRID_AXES.ADDITION
    : BULK_ORDER_GRID_AXES.CYLINDER

export const isAdditionGrid = focalType => getGridAxis(focalType) === BULK_ORDER_GRID_AXES.ADDITION

export const getColumnValue = (line, gridAxis) =>
  gridAxis === BULK_ORDER_GRID_AXES.ADDITION ? line.addition ?? 0 : line.cylinder

export const toCartPowers = (sphere, columnValue, gridAxis) =>
  gridAxis === BULK_ORDER_GRID_AXES.ADDITION
    ? { sphere, cylinder: 0, addition: columnValue }
    : { sphere, cylinder: columnValue, addition: 0 }

/**
 * Convert visible grid cell powers to plus-cylinder cart identity.
 * Addition axis ignores cylinderSign. Minus cylinder view is transposed before store.
 */
export const toStoredCartPowers = (sphere, columnValue, gridAxis, cylinderSign) => {
  const display = toCartPowers(sphere, columnValue, gridAxis)
  if (gridAxis === BULK_ORDER_GRID_AXES.ADDITION || cylinderSign !== BULK_ORDER_GRID_SIGNS.MINUS) {
    return display
  }

  const plus = toPlusCylinderCoords(display.sphere, display.cylinder)
  return { ...display, sphere: plus.sphere, cylinder: plus.cylinder }
}

/** Sphere + column power for cart/recap labels under the active display preference. */
export const getDisplayLinePowers = (line, gridAxis, cylinderSign) => {
  if (gridAxis === BULK_ORDER_GRID_AXES.ADDITION) {
    return { sphere: line.sphere, columnValue: line.addition ?? 0 }
  }

  const display = toDisplayPowers(line.sphere, line.cylinder, cylinderSign)
  return { sphere: display.sphere, columnValue: display.cylinder }
}

/** Map a power value to plus/minus grid sign preference (zero → plus). */
export const getGridSignFromPower = power =>
  Number(power) < 0 ? BULK_ORDER_GRID_SIGNS.MINUS : BULK_ORDER_GRID_SIGNS.PLUS

/** True when the addition axis includes both positive and negative powers. */
export const hasBothAdditionSigns = additions => {
  if (!additions?.length) return false
  return additions.some(value => value > 0) && additions.some(value => value < 0)
}

/**
 * When both addition signs exist, show only columns for the active preference.
 * Zero stays visible in both views. No-op when both signs are not present.
 */
export const filterAdditionGridBySign = (grid, additionSign) => {
  if (!grid?.additions?.length || !hasBothAdditionSigns(grid.additions)) return grid

  const keepNegative = additionSign === BULK_ORDER_GRID_SIGNS.MINUS
  const colIndexes = []
  grid.additions.forEach((addition, index) => {
    if (keepNegative ? addition <= 0 : addition >= 0) colIndexes.push(index)
  })

  if (colIndexes.length === grid.additions.length) return grid

  return {
    ...grid,
    additions: colIndexes.map(index => grid.additions[index]),
    available: (grid.available || []).map(row => colIndexes.map(index => row[index])),
  }
}

export const toQuantityKey = (sphere, columnValue, gridAxis) => {
  const { cylinder, addition } = toCartPowers(sphere, columnValue, gridAxis)
  return getQuantityKey(sphere, cylinder, addition)
}
